import { createBrowserRouter, Navigate } from 'react-router-dom';
import { lazy } from 'react';
import { RootLayout } from '@/layouts/RootLayout';

// Lazy load page components for code splitting
const LandingPage = lazy(() => import('@/pages/LandingPage').then(m => ({ default: m.LandingPage })));
const AnalysisPage = lazy(() => import('@/pages/AnalysisPage').then(m => ({ default: m.AnalysisPage })));
const DemoPage = lazy(() => import('@/pages/DemoPage').then(m => ({ default: m.DemoPage })));
const LibraryPage = lazy(() => import('@/pages/LibraryPage').then(m => ({ default: m.LibraryPage })));
const BlogPage = lazy(() => import('@/pages/BlogPage').then(m => ({ default: m.BlogPage })));
const BlogPostPage = lazy(() => import('@/pages/BlogPostPage').then(m => ({ default: m.BlogPostPage })));
const AboutPage = lazy(() => import('@/pages/AboutPage').then(m => ({ default: m.AboutPage })));

// Auth pages (will be created in Phase 2)
const LoginPage = lazy(() => import('@/pages/auth/LoginPage').then(m => ({ default: m.LoginPage })));
const SignupPage = lazy(() => import('@/pages/auth/SignupPage').then(m => ({ default: m.SignupPage })));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const AuthCallbackPage = lazy(() => import('@/pages/auth/AuthCallbackPage').then(m => ({ default: m.AuthCallbackPage })));

// Dashboard pages (will be created in Phase 5)
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage').then(m => ({ default: m.DashboardPage })));
const AccountSettingsPage = lazy(() => import('@/pages/dashboard/AccountSettingsPage').then(m => ({ default: m.AccountSettingsPage })));
const SubscriptionPage = lazy(() => import('@/pages/dashboard/SubscriptionPage').then(m => ({ default: m.SubscriptionPage })));
const BillingHistoryPage = lazy(() => import('@/pages/dashboard/BillingHistoryPage').then(m => ({ default: m.BillingHistoryPage })));

// Pricing page (will be created in Phase 4)
const PricingPage = lazy(() => import('@/pages/PricingPage').then(m => ({ default: m.PricingPage })));

// Protected route wrapper (will use auth context in Phase 2)
import { ProtectedRoute } from './ProtectedRoute';

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      // Public routes
      { index: true, element: <LandingPage /> },
      { path: 'demo', element: <DemoPage /> },
      { path: 'blog', element: <BlogPage /> },
      { path: 'blog/:id', element: <BlogPostPage /> },
      { path: 'about', element: <AboutPage /> },
      { path: 'pricing', element: <PricingPage /> },

      // Protected routes (require login)
      {
        element: <ProtectedRoute />,
        children: [
          { path: 'analysis', element: <AnalysisPage /> },
          { path: 'library', element: <LibraryPage /> },
        ],
      },

      // Auth routes
      { path: 'login', element: <LoginPage /> },
      { path: 'signup', element: <SignupPage /> },
      { path: 'forgot-password', element: <ForgotPasswordPage /> },
      { path: 'reset-password', element: <ResetPasswordPage /> },
      { path: 'auth/callback', element: <AuthCallbackPage /> },

      // Protected dashboard routes
      {
        path: 'dashboard',
        element: <ProtectedRoute />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'account', element: <AccountSettingsPage /> },
          { path: 'subscription', element: <SubscriptionPage /> },
          { path: 'billing', element: <BillingHistoryPage /> },
        ],
      },

      // Catch-all redirect
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);
